<script setup>
import { computed, ref, shallowRef, watch } from 'vue';
import moment from 'moment';
import Papa from 'papaparse';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import UiFormFields from '@/components/ui/UiFormFields.vue';
import PageTabs from '@/components/Utils/PageTabs.vue';
import { apiRequest, assertOk, errorMessage } from '@/api/http';
import { useToast } from '@/composables/useToast';
import { formatDate } from '@/utils/format';

// Adds students to a class (the adviser's own, or any of an admin's level
// from Rooms): from a CSV (checked row by row before anything is sent), one
// at a time, or picked from last school year's students moving up to (or
// repeating) this grade. New and imported students are matched by LRN on the
// server; the class decides the grade level.
const props = defineProps({
  roomId: { type: Number, default: null },
  roomName: { type: String, default: '' },
  gradeLevel: { type: String, default: '' },
  schoolYearId: { type: [Number, String], default: null },
  existingLrns: { type: Array, default: () => [] }, // students already in the class
});
const open = defineModel({ type: Boolean, default: false });
const emit = defineEmits(['saved']);
const toast = useToast();

const MAX_ROWS = 500;
const MAX_FILE_BYTES = 1024 * 1024;
const MODES = [
  { id: 'file', name: 'Upload a list' },
  { id: 'one', name: 'Add one student' },
  { id: 'returning', name: 'From last school year' },
];
// CSV header (lower case) → field. Extra columns, such as Level and Grade
// in older templates, are ignored.
const COLUMNS = {
  'first name': 'fname',
  'middle name': 'mname',
  'last name': 'lname',
  suffix: 'suffix',
  birthdate: 'bdate',
  'birth date': 'bdate',
  sex: 'sex',
  transferred: 'transfered',
  nationality: 'isFilipino',
  'mobile number': 'mobile_no',
  lrn: 'lrnNo',
};
// Mirrors the server's limits. One overlong value would make the server
// reject the whole list, so such rows are flagged here instead.
const MAX_LENGTH = { lrnNo: 50, fname: 100, mname: 100, lname: 100, suffix: 20, isFilipino: 50, mobile_no: 20 };
const FIELD_LABELS = {
  lrnNo: 'LRN',
  fname: 'First name',
  mname: 'Middle name',
  lname: 'Last name',
  suffix: 'Suffix',
  isFilipino: 'Nationality',
  mobile_no: 'Mobile number',
};
const REQUIRED_COLUMNS = ['First Name', 'Last Name', 'LRN', 'Sex', 'Birthdate'];
const TEMPLATE = [
  'First Name,Middle Name,Last Name,Suffix,Birthdate,Sex,Transferred,Nationality,Mobile Number,LRN',
  'Juan,Santos,Dela Cruz,,2012-06-30,Male,No,Filipino,09171234567,123456789012',
].join('\r\n');
const DATE_FORMATS = ['YYYY-MM-DD', 'M/D/YYYY', 'MM/DD/YYYY', 'M-D-YYYY', 'MMM D, YYYY', 'MMMM D, YYYY', 'D-MMM-YYYY'];

const mode = shallowRef('file');
const saving = shallowRef(false);
const result = shallowRef(null); // the server's summary once saved

// ---- Upload a list -------------------------------------------------------
const fileInput = ref(null);
const dragging = shallowRef(false);
const fileName = shallowRef('');
const fileError = shallowRef('');
const rows = shallowRef([]);

const normalizeLrn = (v) => String(v ?? '').replace(/\s/g, '');
const clean = (v) => String(v ?? '').trim();

function normalizeSex(v) {
  const s = clean(v).toLowerCase();
  if (s === 'm' || s === 'male') return 'Male';
  if (s === 'f' || s === 'female') return 'Female';
  return null;
}

function normalizeDate(v) {
  const d = moment(clean(v), DATE_FORMATS, true);
  return d.isValid() && d.isBefore(moment()) ? d.format('YYYY-MM-DD') : null;
}

function checkRow(raw, seen, inClass) {
  const student = {
    lrnNo: normalizeLrn(raw.lrnNo),
    fname: clean(raw.fname),
    mname: clean(raw.mname),
    lname: clean(raw.lname),
    suffix: clean(raw.suffix),
    bdate: normalizeDate(raw.bdate),
    sex: normalizeSex(raw.sex),
    transfered: ['1', 'yes', 'y', 'true'].includes(clean(raw.transfered).toLowerCase()),
    isFilipino: clean(raw.isFilipino) || 'Filipino',
    mobile_no: clean(raw.mobile_no),
  };
  const problems = [];
  if (!student.lrnNo) problems.push('LRN is missing');
  if (!student.fname) problems.push('First name is missing');
  if (!student.lname) problems.push('Last name is missing');
  if (!student.sex) problems.push('Sex must be Male or Female');
  if (!student.bdate) problems.push(clean(raw.bdate) ? 'Birth date is not a valid date' : 'Birth date is missing');
  Object.entries(MAX_LENGTH).forEach(([key, max]) => {
    if (student[key].length > max) problems.push(`${FIELD_LABELS[key]} is longer than ${max} characters`);
  });
  if (student.lrnNo && seen.has(student.lrnNo)) problems.push('LRN is listed more than once');
  seen.add(student.lrnNo);

  let status = 'ready';
  if (problems.length) status = 'invalid';
  else if (inClass.has(student.lrnNo)) status = 'inClass';
  return { student, status, problem: problems.join(' · ') };
}

function readFile(file) {
  resetFile();
  if (!file) return;
  fileName.value = file.name;
  if (!/\.csv$/i.test(file.name)) {
    fileError.value = 'Choose a .csv file. In Excel or Google Sheets, use File → Save as / Download → CSV.';
    return;
  }
  if (file.size > MAX_FILE_BYTES) {
    fileError.value = 'This file is larger than 1 MB. Split it into smaller lists.';
    return;
  }
  Papa.parse(file, {
    header: true,
    skipEmptyLines: 'greedy',
    transformHeader: (h) => h.replace(/^\uFEFF/, '').trim().toLowerCase(),
    complete: ({ data, meta }) => {
      const headers = meta.fields || [];
      const missing = REQUIRED_COLUMNS.filter(
        (c) => !headers.includes(c.toLowerCase()) && !(c === 'Birthdate' && headers.includes('birth date')),
      );
      if (missing.length) {
        fileError.value = `This file is missing the ${missing.join(', ')} column${missing.length > 1 ? 's' : ''}. Download the template to see the expected columns.`;
        return;
      }
      if (!data.length) {
        fileError.value = 'This file has no students in it.';
        return;
      }
      if (data.length > MAX_ROWS) {
        fileError.value = `This file has ${data.length} rows. Add up to ${MAX_ROWS} at a time.`;
        return;
      }
      const seen = new Set();
      const inClass = new Set(props.existingLrns.map(normalizeLrn));
      rows.value = data.map((line, index) => {
        const raw = {};
        Object.entries(COLUMNS).forEach(([header, key]) => {
          if (line[header] !== undefined && raw[key] === undefined) raw[key] = line[header];
        });
        return { id: index + 1, ...checkRow(raw, seen, inClass) };
      });
    },
    error: () => {
      fileError.value = 'This file could not be read. Save it as CSV and try again.';
    },
  });
}

function resetFile() {
  fileName.value = '';
  fileError.value = '';
  rows.value = [];
  if (fileInput.value) fileInput.value.value = '';
}

function onDrop(event) {
  dragging.value = false;
  readFile(event.dataTransfer?.files?.[0]);
}

function downloadTemplate() {
  const url = URL.createObjectURL(new Blob([TEMPLATE], { type: 'text/csv;charset=utf-8' }));
  const link = Object.assign(document.createElement('a'), { href: url, download: 'student-list-template.csv' });
  link.click();
  URL.revokeObjectURL(url);
}

const counts = computed(() => {
  const c = { ready: 0, inClass: 0, invalid: 0 };
  rows.value.forEach((r) => c[r.status]++);
  return c;
});
// Problems first, so they're seen without paging.
const STATUS_ORDER = { invalid: 0, ready: 1, inClass: 2 };
const sortedRows = computed(() =>
  [...rows.value].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.id - b.id),
);
const STATUS = {
  ready: { label: 'Ready', variant: 'green' },
  inClass: { label: 'Already in class', variant: 'secondary' },
  invalid: { label: 'Needs fixing', variant: 'red' },
};
const previewHeaders = [
  { title: 'Row', key: 'id', width: 64 },
  { title: 'Name', key: 'name', sortable: false },
  { title: 'LRN', key: 'student.lrnNo', sortable: false },
  { title: 'Sex', key: 'student.sex', sortable: false },
  { title: 'Birth date', key: 'student.bdate', sortable: false },
  { title: 'Status', key: 'status', sortable: false },
];
const fullName = (s) => [s.lname && `${s.lname},`, s.fname, s.mname && `${s.mname[0]}.`, s.suffix].filter(Boolean).join(' ');

// ---- Add one student -----------------------------------------------------
const formRef = ref(null);
const blankStudent = () => ({
  lrnNo: '', fname: '', mname: '', lname: '', suffix: '', bdate: '', sex: null, transfered: false, mobile_no: '',
});
const single = ref(blankStudent());
const notInClass = (v) => !props.existingLrns.map(normalizeLrn).includes(normalizeLrn(v)) || 'This student is already in this class.';
const maxLength = (key) => (v) =>
  clean(v).length <= MAX_LENGTH[key] || `Use ${MAX_LENGTH[key]} characters or fewer.`;
const singleFields = [
  { key: 'lrnNo', label: 'LRN', required: true, rules: [notInClass, maxLength('lrnNo')], placeholder: '12-digit Learner Reference Number', span: 2 },
  { key: 'fname', label: 'First name', required: true, rules: [maxLength('fname')] },
  { key: 'lname', label: 'Last name', required: true, rules: [maxLength('lname')] },
  { key: 'mname', label: 'Middle name', rules: [maxLength('mname')] },
  { key: 'suffix', label: 'Suffix', placeholder: 'Jr., III', rules: [maxLength('suffix')] },
  { key: 'sex', label: 'Sex', type: 'select', required: true, items: ['Male', 'Female'].map((v) => ({ title: v, value: v })) },
  { key: 'bdate', label: 'Birth date', type: 'date', required: true, max: moment().format('YYYY-MM-DD') },
  { key: 'transfered', label: 'Transferee', type: 'select', items: [{ title: 'No', value: false }, { title: 'Yes', value: true }] },
  { key: 'mobile_no', label: 'Mobile number', placeholder: '09XX XXX XXXX', rules: [maxLength('mobile_no')] },
];

// ---- From last school year -------------------------------------------------
const candidates = shallowRef([]);
const previousYear = shallowRef(null);
const candidatesLoaded = shallowRef(false);
const candidatesLoading = shallowRef(false);
const candidatesError = shallowRef('');
const picked = ref([]);
const search = shallowRef('');
const sectionFilter = shallowRef(null);

async function loadCandidates() {
  candidatesLoading.value = true;
  candidatesError.value = '';
  try {
    const body = assertOk(
      await apiRequest('get', `/enroll-student/returningCandidates/${props.roomId}/${Number(props.schoolYearId)}`),
    );
    candidates.value = body.students || [];
    previousYear.value = body.previousYear;
    candidatesLoaded.value = true;
  } catch (e) {
    candidatesError.value = errorMessage(e, 'Could not load last school year’s students.');
  } finally {
    candidatesLoading.value = false;
  }
}
watch(mode, (m) => {
  if (m === 'returning' && !candidatesLoaded.value && !candidatesLoading.value) loadCandidates();
});

// Repeating = placed in this same grade last year.
const isRepeating = (c) => c.previousGrade === props.gradeLevel;
const sectionLabel = (c) => [c.previousGrade, c.previousSection].filter(Boolean).join(' · ');
const sectionOptions = computed(() =>
  [...new Map(candidates.value.map((c) => [c.previousRoomId, sectionLabel(c)])).entries()]
    .map(([value, title]) => ({ value, title }))
    .sort((a, b) => a.title.localeCompare(b.title)),
);
const shownCandidates = computed(() => {
  const q = search.value.trim().toLowerCase();
  return candidates.value.filter(
    (c) =>
      (!sectionFilter.value || c.previousRoomId === sectionFilter.value) &&
      (!q || `${fullName(c)} ${c.lrnNo}`.toLowerCase().includes(q)),
  );
});
// The usual picks: students who passed last year's lower grade.
function selectPassed() {
  const ids = shownCandidates.value.filter((c) => c.passed && !isRepeating(c)).map((c) => c.id);
  picked.value = [...new Set([...picked.value, ...ids])];
}
// A flag when the pick goes against last year's result; the adviser decides.
function resultNote(c) {
  if (c.passed === false && !isRepeating(c)) return `Failed ${c.previousGrade}`;
  if (c.passed === true && isRepeating(c)) return `Passed ${c.previousGrade}`;
  return '';
}
const candidateHeaders = [
  { title: 'Name', key: 'name', sortable: false },
  { title: 'LRN', key: 'lrnNo', sortable: false },
  { title: 'Last school year', key: 'previous', sortable: false },
  { title: 'General average', key: 'generalAverage', align: 'end' },
  { title: 'Result', key: 'passed', sortable: false },
];

// ---- Save ------------------------------------------------------------------
const toSend = computed(() =>
  mode.value === 'one'
    ? [{ ...single.value, lrnNo: normalizeLrn(single.value.lrnNo), isFilipino: 'Filipino' }]
    : rows.value.filter((r) => r.status === 'ready').map((r) => r.student),
);
const submitText = computed(() => {
  if (mode.value === 'one') return 'Add student';
  const n = mode.value === 'returning' ? picked.value.length : counts.value.ready;
  return n ? `Add ${n} student${n === 1 ? '' : 's'}` : 'Add students';
});
const canSubmit = computed(() => {
  if (mode.value === 'returning') return picked.value.length > 0;
  return mode.value === 'one' || counts.value.ready > 0;
});

// Optional fields go out only when filled in.
const compact = (student) => Object.fromEntries(Object.entries(student).filter(([, v]) => v !== '' && v !== null));

async function submit() {
  if (mode.value === 'one') {
    const { valid } = await formRef.value.validate();
    if (!valid) return;
  }
  saving.value = true;
  try {
    const target = { roomId: props.roomId, schoolYearId: Number(props.schoolYearId) };
    const body = assertOk(
      mode.value === 'returning'
        ? await apiRequest('post', '/enroll-student/pullStudents', { ...target, studentIds: picked.value })
        : await apiRequest('post', '/enroll-student/importStudent', { ...target, students: toSend.value.map(compact) }),
    );
    if (body.successCount) emit('saved');
    const notAdded = [
      ...(body.skipped || []),
      ...(body.alreadyInClass || []).map((name) => ({ name, reason: 'Already in this class' })),
    ];
    if (!notAdded.length) {
      toast.success(body.msg);
      open.value = false;
      return;
    }
    result.value = { added: body.successCount || 0, notAdded };
  } catch (e) {
    toast.error(errorMessage(e, 'Could not add the students.'));
  } finally {
    saving.value = false;
  }
}

// Each opening starts fresh.
watch(open, (isOpen) => {
  if (!isOpen) return;
  mode.value = 'file';
  result.value = null;
  single.value = blankStudent();
  resetFile();
  candidates.value = [];
  candidatesLoaded.value = false;
  picked.value = [];
  search.value = '';
  sectionFilter.value = null;
});
</script>

<template>
  <UiDialog
    v-model="open"
    :title="result ? (result.added ? 'Students added' : 'No students added') : 'Add students'"
    :description="result ? '' : [roomName, gradeLevel].filter(Boolean).join(' · ')"
    :size="!result && ((mode === 'file' && rows.length) || mode === 'returning') ? 'xl' : 'lg'"
    :hide-close="saving"
  >
    <!-- Summary after saving, when some students weren't added -->
    <div v-if="result" class="add-students">
      <v-alert type="info" variant="tonal" density="compact">
        <template v-if="result.added">
          Added {{ result.added }} student{{ result.added === 1 ? '' : 's' }}.
        </template>
        {{ result.notAdded.length }} {{ result.notAdded.length === 1 ? "wasn't" : "weren't" }} added:
      </v-alert>
      <ul class="add-students__skipped">
        <li v-for="(item, i) in result.notAdded" :key="i">
          <span class="font-weight-medium">{{ item.name }}</span>
          <span v-if="item.lrnNo" class="text-medium-emphasis"> · {{ item.lrnNo }}</span>
          <span class="text-medium-emphasis"> — {{ item.reason }}</span>
        </li>
      </ul>
    </div>

    <div v-else class="add-students">
      <PageTabs v-model="mode" :items="MODES" class="align-self-start" />

      <!-- Upload a list -->
      <template v-if="mode === 'file'">
        <div
          v-if="!rows.length"
          class="add-students__drop"
          :class="{ 'add-students__drop--active': dragging }"
          role="button"
          tabindex="0"
          @click="fileInput.click()"
          @keydown.enter.prevent="fileInput.click()"
          @keydown.space.prevent="fileInput.click()"
          @dragover.prevent="dragging = true"
          @dragleave.prevent="dragging = false"
          @drop.prevent="onDrop"
        >
          <v-icon size="28" class="text-medium-emphasis">mdi-file-upload-outline</v-icon>
          <p class="add-students__drop-title">
            Drop a CSV file here, or <span class="text-primary">browse</span>
          </p>
          <p class="add-students__hint">
            One student per row, up to {{ MAX_ROWS }} at a time. Required columns: {{ REQUIRED_COLUMNS.join(', ') }}.
          </p>
          <input ref="fileInput" type="file" accept=".csv,text/csv" hidden @change="readFile($event.target.files[0])" />
        </div>

        <v-alert v-if="fileError" type="error" variant="tonal" density="compact">
          <span v-if="fileName" class="font-weight-medium">{{ fileName }}: </span>{{ fileError }}
        </v-alert>

        <template v-if="rows.length">
          <div class="add-students__file">
            <v-icon size="18">mdi-file-delimited-outline</v-icon>
            <span class="font-weight-medium text-truncate">{{ fileName }}</span>
            <span class="text-medium-emphasis">· {{ rows.length }} row{{ rows.length === 1 ? '' : 's' }}</span>
            <v-spacer />
            <v-btn variant="text" size="small" prepend-icon="mdi-swap-horizontal" @click="resetFile">Change file</v-btn>
          </div>

          <div class="add-students__counts">
            <UiBadge variant="green">{{ counts.ready }} ready</UiBadge>
            <UiBadge v-if="counts.invalid" variant="red">{{ counts.invalid }} need fixing</UiBadge>
            <UiBadge v-if="counts.inClass" variant="secondary">{{ counts.inClass }} already in class</UiBadge>
          </div>
          <p v-if="counts.invalid" class="add-students__hint text-left">
            Rows that need fixing are left out. Fix them in the file and upload it again to add them.
          </p>

          <v-data-table
            :headers="previewHeaders"
            :items="sortedRows"
            :items-per-page="10"
            :hide-default-footer="rows.length <= 10"
            density="compact"
            class="add-students__table"
          >
            <template #[`item.name`]="{ item }">{{ fullName(item.student) }}</template>
            <template #[`item.student.bdate`]="{ item }">{{ formatDate(item.student.bdate) || '—' }}</template>
            <template #[`item.student.sex`]="{ item }">{{ item.student.sex || '—' }}</template>
            <template #[`item.status`]="{ item }">
              <UiBadge :variant="STATUS[item.status].variant">{{ STATUS[item.status].label }}</UiBadge>
              <div v-if="item.problem" class="add-students__problem">{{ item.problem }}</div>
            </template>
          </v-data-table>
        </template>
      </template>

      <!-- From last school year -->
      <template v-else-if="mode === 'returning'">
        <div v-if="candidatesLoading" class="d-flex justify-center py-8">
          <v-progress-circular indeterminate size="28" />
        </div>
        <v-alert v-else-if="candidatesError" type="error" variant="tonal" density="compact">
          {{ candidatesError }}
          <template #append>
            <v-btn variant="text" size="small" @click="loadCandidates">Try again</v-btn>
          </template>
        </v-alert>
        <p v-else-if="!previousYear" class="add-students__hint text-left">
          There's no school year before this one, so there are no returning students to pick from. Add students
          from a list or one at a time instead.
        </p>
        <p v-else-if="!candidates.length" class="add-students__hint text-left">
          No students from {{ previousYear }} are waiting for a section. Everyone moving up to or repeating
          {{ gradeLevel }} is already in a class this school year. Add new students and transferees from a list
          or one at a time.
        </p>
        <template v-else>
          <p class="add-students__hint text-left">
            Students from {{ previousYear }} who are moving up to or repeating {{ gradeLevel }} and don't have a
            section yet this school year. Each one can be added to only one section.
          </p>
          <div class="add-students__filters">
            <v-text-field
              v-model="search"
              placeholder="Search name or LRN"
              prepend-inner-icon="mdi-magnify"
              density="compact"
              hide-details
              clearable
            />
            <v-select
              v-model="sectionFilter"
              :items="sectionOptions"
              placeholder="All sections"
              density="compact"
              hide-details
              clearable
            />
            <v-btn variant="outlined" prepend-icon="mdi-check-all" @click="selectPassed">Select all who passed</v-btn>
          </div>
          <div class="add-students__counts">
            <UiBadge variant="secondary">{{ candidates.length }} available</UiBadge>
            <UiBadge v-if="picked.length" variant="green">{{ picked.length }} selected</UiBadge>
            <v-btn v-if="picked.length" variant="text" size="x-small" @click="picked = []">Clear</v-btn>
          </div>
          <v-data-table
            v-model="picked"
            :headers="candidateHeaders"
            :items="shownCandidates"
            item-value="id"
            show-select
            :items-per-page="10"
            :hide-default-footer="shownCandidates.length <= 10"
            density="compact"
            class="add-students__table"
          >
            <template #[`item.name`]="{ item }">{{ fullName(item) }}</template>
            <template #[`item.previous`]="{ item }">{{ sectionLabel(item) || '—' }}</template>
            <template #[`item.generalAverage`]="{ item }">
              {{ item.generalAverage === null ? '—' : item.generalAverage.toFixed(2) }}
            </template>
            <template #[`item.passed`]="{ item }">
              <UiBadge v-if="item.passed === null" variant="secondary">No grades</UiBadge>
              <UiBadge v-else :variant="item.passed ? 'green' : 'red'">{{ item.passed ? 'Passed' : 'Failed' }}</UiBadge>
              <div v-if="resultNote(item)" class="add-students__problem">{{ resultNote(item) }}</div>
            </template>
            <template #no-data>
              <span class="text-medium-emphasis">No students match.</span>
            </template>
          </v-data-table>
        </template>
      </template>

      <!-- Add one student -->
      <v-form v-else ref="formRef" class="form-grid" @submit.prevent="submit">
        <UiFormFields v-model:values="single" :fields="singleFields" />
      </v-form>
    </div>

    <template #footer="{ close }">
      <template v-if="result">
        <v-btn color="primary" @click="close">Done</v-btn>
      </template>
      <template v-else>
        <v-btn v-if="mode === 'file'" variant="text" prepend-icon="mdi-download" class="mr-auto" @click="downloadTemplate">
          Download template
        </v-btn>
        <v-btn variant="outlined" :disabled="saving" @click="close">Cancel</v-btn>
        <v-btn color="primary" :loading="saving" :disabled="!canSubmit" @click="submit">{{ submitText }}</v-btn>
      </template>
    </template>
  </UiDialog>
</template>

<style scoped>
.add-students {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.add-students__drop {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 32px 16px;
  border: 1px dashed var(--border);
  border-radius: 8px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}
.add-students__drop:hover,
.add-students__drop--active {
  border-color: rgb(var(--brand-rgb));
  background-color: rgba(var(--brand-rgb), 0.04);
}
.add-students__drop:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(var(--brand-rgb), 0.3);
}
.add-students__drop-title {
  margin: 4px 0 0;
  font-size: 14px;
  font-weight: 500;
}
.add-students__hint {
  margin: 0;
  font-size: 13px;
  color: var(--muted-foreground);
}

.add-students__file {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 8px 8px 8px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
}
.add-students__counts {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}
.add-students__filters {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 220px) auto;
  gap: 8px;
}
@media (max-width: 640px) {
  .add-students__filters {
    grid-template-columns: 1fr;
  }
}
.add-students__table {
  border: 1px solid var(--border);
  border-radius: 8px;
}
.add-students__problem {
  margin-top: 2px;
  font-size: 12px;
  color: rgb(var(--v-theme-error));
}

.add-students__skipped {
  margin: 0;
  padding-left: 18px;
  font-size: 14px;
  line-height: 1.8;
}
</style>
